import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons/faSquareCheck';
import { faSquare } from '@fortawesome/pro-light-svg-icons/faSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { FunctionComponent, useMemo, useState } from 'react';
import { SelectField } from 'components/UI';
import { useAuth, useMasterData } from 'hooks';
import { Option, OWNER_ID } from '@spike/model';

export interface Location {
    marketplace: { id: number; name: string };
    roleId?: number;
}

interface SelectLocationProps {
    location: Location;
    selected: boolean;
    disabledLocation?: boolean;
    disableRole?: boolean;
    hideRole?: boolean;
    hideBottomBorder?: boolean;
    onChange?: (location: Location) => void;
    onSelect?: (location: Location) => void;
    onUnselect?: (marketplaceId: number) => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            paddingTop: '18px',
            gap: '21px'
        },
        bottomBorder: {
            borderBottom: '1px solid #D4D4D4',
            paddingBottom: '18px'
        },
        icon: {
            width: '21px',
            height: '21px',
            marginRight: '7px'
        },
        label: {
            color: '#000',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '100%'
        },
        row: {
            display: 'flex',
            alignItems: 'center'
        },
        clickable: {
            display: 'flex',
            cursor: 'pointer',
            alignItems: 'center'
        },
        disabled: {
            'cursor': 'default',
            '& $icon': {
                color: '#7A7A7A'
            },
            '& span': {
                color: '#7A7A7A'
            }
        },
        role: {
            [theme.breakpoints.up('md')]: {
                width: '100%'
            }
        }
    })
);

export const SelectLocation: FunctionComponent<SelectLocationProps> = props => {
    const classes = useStyles();

    const masterData = useMasterData();
    const auth = useAuth();

    const userIsOwner = auth.user!.role.id === OWNER_ID;
    const staffIsOwner = props.location.roleId === OWNER_ID;

    const roles = useMemo(
        () =>
            (userIsOwner && staffIsOwner
                ? masterData.roles
                : masterData.roles.filter(role => role.id !== OWNER_ID)
            ).sort((role, otherRole) =>
                role.name.toLocaleLowerCase().localeCompare(otherRole.name.toLocaleLowerCase())
            ),
        [auth.user?.role.id, masterData.roles, props.location.roleId]
    );

    const clickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault;
        event.stopPropagation();

        if (!props.disabledLocation) {
            props.selected ? props.onUnselect?.(props.location.marketplace.id) : props.onSelect?.(props.location);
        }
    };

    const changeRoleHandler = (option: Option<string | number>) => {
        props.onChange?.({ ...props.location, roleId: Number(option.id) });
    };

    return (
        <div className={clsx(classes.container, { [classes.bottomBorder]: !props.hideBottomBorder }, props.className)}>
            <div
                className={classes.row}
                onClick={clickHandler}
            >
                <div className={clsx(classes.clickable, { [classes.disabled]: props.disabledLocation })}>
                    <FontAwesomeIcon
                        icon={props.selected ? faSquareCheck : faSquare}
                        className={classes.icon}
                    />
                    <span className={classes.label}>{props.location.marketplace.name}</span>
                </div>
            </div>
            {props.selected && !props.hideRole && (
                <>
                    <div className={classes.row}>
                        <div className={clsx(classes.role)}>
                            <SelectField
                                id="staff_add_member_company_input_role"
                                label="Role"
                                placeholder="Role"
                                selectedOption={masterData.roles.find(r => r.id === props.location.roleId)}
                                options={roles}
                                onSelect={changeRoleHandler}
                                disabled={props.disableRole}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SelectLocation;
